"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import styles from "./ChatBubble.module.css";

const HIDDEN_PATHS = ["/video/", "/editor/"];

function ChatIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 5.75C4 4.78 4.78 4 5.75 4h12.5c.97 0 1.75.78 1.75 1.75v8.5c0 .97-.78 1.75-1.75 1.75h-7.68l-4.2 3.14A.85.85 0 0 1 5 18.46V16h-.25A1.75 1.75 0 0 1 3 14.25v-8.5Zm2 .25v10.76L9.9 13.9c.15-.1.32-.16.5-.16H18V6H6Z" />
    </svg>
  );
}

export default function ChatBubble({ userId }) {
  const [Panel, setPanel] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [requestedOpen, setRequestedOpen] = useState(false);
  const [showNudge, setShowNudge] = useState(false);
  const loadingRef = useRef(null);
  const mountedRef = useRef(false);

  const loadPanel = useCallback(() => {
    if (loadingRef.current) return loadingRef.current;

    loadingRef.current = import("./ChatBubblePanel").then((mod) => {
      if (mountedRef.current) {
        setPanel(() => mod.default);
      }
      return mod.default;
    });

    return loadingRef.current;
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const check = () => {
      const path = window.location.pathname;
      setHidden(HIDDEN_PATHS.some((p) => path.startsWith(p)));
    };

    check();
    window.addEventListener("popstate", check);
    return () => window.removeEventListener("popstate", check);
  }, []);

  useEffect(() => {
    const runIdleLoad = () => {
      loadPanel().catch((error) => {
        console.error("[ChatBubble] Failed to preload chat panel:", error);
      });
    };

    if ("requestIdleCallback" in window) {
      const idleId = window.requestIdleCallback(runIdleLoad, { timeout: 9000 });
      return () => window.cancelIdleCallback?.(idleId);
    }

    const timeoutId = window.setTimeout(runIdleLoad, 7000);
    return () => window.clearTimeout(timeoutId);
  }, [loadPanel]);

  useEffect(() => {
    if (requestedOpen) {
      setShowNudge(false);
      return;
    }

    const dismissed = sessionStorage.getItem("flare-chat-nudge-dismissed");
    if (dismissed) return;
    const timer = window.setTimeout(() => setShowNudge(true), 3000);
    return () => window.clearTimeout(timer);
  }, [requestedOpen]);

  const dismissNudge = useCallback(() => {
    setShowNudge(false);
    sessionStorage.setItem("flare-chat-nudge-dismissed", "1");
  }, []);

  const openChat = useCallback(() => {
    setRequestedOpen(true);
    dismissNudge();
    loadPanel().catch((error) => {
      console.error("[ChatBubble] Failed to load chat panel:", error);
    });
  }, [dismissNudge, loadPanel]);

  if (Panel) {
    return <Panel userId={userId} initialOpen={requestedOpen} />;
  }

  if (hidden) return null;

  return (
    <>
      {requestedOpen ? (
        <div className={`${styles.panel} ${styles.loadingPanel}`} role="status" aria-live="polite">
          <div className={styles.header}>
            <div className={styles.headerContent}>
              <p className={styles.headerTitle}>Support</p>
              <p className={styles.headerSubtext}>Loading chat...</p>
            </div>
          </div>
        </div>
      ) : null}
      <div className={styles.overlay}>
        {showNudge ? (
          <div className={styles.nudge}>
            <button type="button" className={styles.nudgeContent} onClick={openChat}>
              Need help?
            </button>
            <button
              type="button"
              className={styles.nudgeDismiss}
              onClick={dismissNudge}
              aria-label="Dismiss chat prompt"
            >
              ×
            </button>
          </div>
        ) : null}
        <button
          type="button"
          className={styles.bubble}
          onClick={openChat}
          onMouseEnter={loadPanel}
          onFocus={loadPanel}
          aria-label="Open support chat"
        >
          <ChatIcon />
        </button>
      </div>
    </>
  );
}
